import { orvalClient } from "@api/client";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type { AIAnalysis } from "./types";

const MAX_ATTEMPTS = 30;
const POLL_INTERVAL = 3000;

export const useAnalysisPolling = (id?: string) => {
  const attempts = useRef(0);

  // Reset attempts when id changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset when polling target changes
  useEffect(() => {
    attempts.current = 0;
  }, [id]);

  const query = useQuery({
    queryKey: ["ai-analyses", "polling", id],
    queryFn: async () => {
      attempts.current += 1;
      const { data } = await orvalClient.get<AIAnalysis>(`/api/ai-analyses/${id}`);
      return data;
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return POLL_INTERVAL;
      if (data.status !== "pending") return false;
      if (attempts.current >= MAX_ATTEMPTS) return false;
      return POLL_INTERVAL;
    },
  });

  const isPolling = !!id && query.data?.status === "pending" && attempts.current < MAX_ATTEMPTS;
  const isTimeout = query.data?.status === "pending" && attempts.current >= MAX_ATTEMPTS;

  return { ...query, isPolling, isTimeout };
};
